import { useState } from 'react';
import type { ImportResult, RiskRating } from '../types';

function ImportExport() {
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  
  const [exportRatings, setExportRatings] = useState<RiskRating[]>([]);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('csv');
  const [exporting, setExporting] = useState(false);

  const handleImport = async () => {
    if (!importFile) return;

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await fetch('/api/import-csv', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      setImportResult(result);
    } catch (error) {
      alert('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);

    try {
      const response = await fetch('/api/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating_filter: exportRatings.length > 0 ? exportRatings : undefined,
          include_whitelisted: false,
          format: exportFormat,
        }),
      });

      if (exportFormat === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `addresses_export_${Date.now()}.csv`;
        a.click();
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: 'application/json',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `addresses_export_${Date.now()}.json`;
        a.click();
      }
    } catch (error) {
      alert('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Import / Export</h2>
        <p className="text-slate-400">
          Bulk import known abusers or export address lists
        </p>
      </div>

      {/* Import Section */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">
          Import Known Abusers (CSV)
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              CSV File Format
            </label>
            <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 font-mono text-sm text-slate-300">
              <div>address,reason,severity,notes</div>
              <div className="text-slate-500">
                0x1234...,Sybil farming,10,Detected multiple wallets
              </div>
              <div className="text-slate-500">
                0x5678...,Token dumping,8,Large dump detected
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-600 file:text-white hover:file:bg-primary-700 file:cursor-pointer"
            />
          </div>

          <button
            onClick={handleImport}
            disabled={importing || !importFile}
            className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
          >
            {importing ? 'Importing...' : 'Import Addresses'}
          </button>

          {importResult && (
            <div className="mt-4 p-4 bg-slate-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Import Results</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <p className="text-success-400 text-2xl font-bold">
                    {importResult.success}
                  </p>
                  <p className="text-slate-400 text-sm">Successful</p>
                </div>
                <div>
                  <p className="text-danger-400 text-2xl font-bold">
                    {importResult.failed}
                  </p>
                  <p className="text-slate-400 text-sm">Failed</p>
                </div>
              </div>
              {importResult.errors.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto">
                  <p className="text-slate-300 text-sm font-medium mb-2">
                    Errors:
                  </p>
                  {importResult.errors.map((err, idx) => (
                    <div
                      key={idx}
                      className="text-xs text-slate-400 mb-1 font-mono"
                    >
                      Row {err.row}: {err.address} - {err.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">
          Export Address List
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Filter by Risk Rating (leave empty for all)
            </label>
            <div className="flex flex-wrap gap-2">
              {(['safe', 'low', 'medium', 'high', 'critical'] as RiskRating[]).map(
                (rating) => (
                  <label
                    key={rating}
                    className="flex items-center space-x-2 px-3 py-2 bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-600"
                  >
                    <input
                      type="checkbox"
                      checked={exportRatings.includes(rating)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setExportRatings([...exportRatings, rating]);
                        } else {
                          setExportRatings(
                            exportRatings.filter((r) => r !== rating)
                          );
                        }
                      }}
                      className="rounded text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-slate-300 text-sm capitalize">
                      {rating}
                    </span>
                  </label>
                )
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Export Format
            </label>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="csv"
                  checked={exportFormat === 'csv'}
                  onChange={() => setExportFormat('csv')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-slate-300">CSV</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="json"
                  checked={exportFormat === 'json'}
                  onChange={() => setExportFormat('json')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-slate-300">JSON</span>
              </label>
            </div>
          </div>

          <button
            onClick={handleExport}
            disabled={exporting}
            className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-700 text-white font-medium rounded-lg transition-colors"
          >
            {exporting ? 'Exporting...' : 'Export Addresses'}
          </button>
        </div>
      </div>
    </div>
  );
}
export default ImportExport;
